import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Icon, Modal } from "semantic-ui-react";
import moment from "moment";

// UX
import CardAffiliateSettingsUX from "react-lib/apps/MEH/CardAffiliateSettingsUX";

// common
import SearchBoxDropdown from "react-lib/appcon/common/SearchBoxDropdown";
import TimeFreeTextBox24 from "react-lib/apps/common/TimeFreeTextBox24";
import DateTextBox from "react-lib/apps/common/DateTextBox";
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";
import { convertBEDateToADDashDate } from "react-lib/utils/dateUtils";
import { AffiliateSettingsType, BTN_ACTS } from "react-lib/apps/MEH/sequence/AffiliateSettings";

// scss
import "./CardAffiliateSettings.scss";

type CardAffiliateSettingsProps = {
  runSequence: any;
  setProp: any;
  onEvent: any;
  errorMessage?: Record<string, any>;

  searchedItemListWithKey?: Record<string, any>;
  AffiliateSettingsSequence?: AffiliateSettingsType;

  languageUX: string;
  masterOptions?: Record<string, any[]>;
  masterData?: Record<string, any[]>;
  buttonLoadCheck?: Record<string, any>;
};

const AFFILIATE_KEY = "Affiliate_Group";

const CardAffiliateSettings = (props: CardAffiliateSettingsProps) => {
  const [openModalDate, setOpenModalDate] = useState<boolean>(false);

  useEffect(() => {
    props.runSequence({
      sequence: "AffiliateSettings",
      restart: true,
    });
  }, []);

  useEffect(() => {
    props.setProp(`searchedItemListWithKey.${AFFILIATE_KEY}`, []);
  }, []);

  const mapOptions = useCallback((items: any[]) => {
    return items.map((item: any) => ({
      key: item.id,
      value: item.id,
      text: `[${item.code}] ${item.name}`,
    }));
  }, []);

  const affiliateGroupListViewOption = useMemo(() => {
    return (props.masterData?.affiliateGroupListView || []).map((item: any) => ({
      key: item.id,
      value: item.id,
      text: `[${item.code}] ${item.name}`,
    }));
  }, [props.masterData?.affiliateGroupListView]);

  const selectedAffiliate = useMemo(
    () => props.AffiliateSettingsSequence?.filterAffiliate?.searchAffiliate || null,
    [props.AffiliateSettingsSequence?.filterAffiliate?.searchAffiliate],
  );

  const handleChangeProduct = useCallback(
    (
      sourceIndex: number,
      field: "startDate" | "startTime" | "endDate" | "endTime" | "active",
      value: string | boolean,
    ) => {
      const products = props.AffiliateSettingsSequence?.affiliateProduct || [];

      const item = products?.[sourceIndex];

      if (!item) {
        return;
      }

      let changes: Record<string, any> = {};
      // active
      if (field === "active") {
        changes = {
          active: value,
        };
      } else {
        const targetField = field.startsWith("start") ? "started_at" : "ended_at";

        const current = item[targetField]
          ? moment.parseZone(item[targetField])
          : moment().startOf("day");

        if (!current.isValid()) {
          console.warn("Invalid datetime:", item[targetField]);
          return;
        }

        // Date
        if (field === "startDate" || field === "endDate") {
          const adDate = convertBEDateToADDashDate(String(value));

          if (!adDate) {
            return;
          }

          const date = moment(adDate, "YYYY-MM-DD", true);

          current.year(date.year()).month(date.month()).date(date.date());
        }

        // Time
        if (field === "startTime" || field === "endTime") {
          const time = moment(String(value), "HH:mm", true);

          if (!time.isValid()) {
            return;
          }

          current.hour(time.hour()).minute(time.minute()).second(0);
        }

        changes = {
          [targetField]: current.format("YYYY-MM-DDTHH:mm:ssZ"),
        };
      }

      // Updated Products
      const currentUpdatedProducts =
        props.AffiliateSettingsSequence?.updatedAffiliateProducts || [];

      const exists = currentUpdatedProducts.some(
        (updated: any) => updated.sourceIndex === sourceIndex,
      );

      const updatedAffiliateProducts = exists
        ? currentUpdatedProducts
        : [
            ...currentUpdatedProducts,
            {
              id: item.id ?? null,
              sourceIndex,
            },
          ];

      // Updated State
      const newProducts = products.map((product: any, index: number) =>
        index === sourceIndex
          ? {
              ...product,
              ...changes,
            }
          : product,
      );

      props.setProp("AffiliateSettingsSequence", {
        ...props.AffiliateSettingsSequence,
        affiliateProduct: newProducts,
        updatedAffiliateProducts,
      });
    },
    [props.AffiliateSettingsSequence, props.setProp],
  );

  const affiliateProductData = useMemo(() => {
    const products = props.AffiliateSettingsSequence?.affiliateProduct || [];

    const searchProduct = props.AffiliateSettingsSequence?.filterAffiliate?.searchProduct;

    return products
      .map((item: any, sourceIndex: number) => ({
        item,
        sourceIndex,
      }))
      .filter(({ item }) => {
        if (!searchProduct) return true;

        const search = searchProduct.toLowerCase();

        return (
          item?.product_code?.toLowerCase().includes(search) ||
          item?.product_name?.toLowerCase().includes(search)
        );
      })
      .map(({ item, sourceIndex }) => {
        const startDate = item?.started_at
          ? moment.parseZone(item.started_at).format("DD/MM/YYYY")
          : "";
        const startTime = item?.started_at ? moment.parseZone(item.started_at).format("HH:mm") : "";
        const endDate = item?.ended_at ? moment.parseZone(item.ended_at).format("DD/MM/YYYY") : "";
        const endTime = item?.ended_at ? moment.parseZone(item.ended_at).format("HH:mm") : "";

        return {
          ...item,
          sourceIndex,
          productCode: <div style={{ paddingLeft: "15px" }}>{item?.product_code}</div>,
          productName: <div style={{ paddingLeft: "15px" }}>{item?.product_name}</div>,
          startedAt: (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 5,
              }}
            >
              <DateTextBox
                id={`start-date-${sourceIndex}`}
                name="startDate"
                useAdDate
                inputStyle={{ width: "135px" }}
                disabled={!item.active}
                placeholder={"mm/dd/yyyy"}
                onChange={(value) => handleChangeProduct(sourceIndex, "startDate", value)}
                value={startDate}
              />
              <TimeFreeTextBox24
                id={`start-time-${sourceIndex}`}
                className="affiliate-time-picker"
                name="startTime"
                autoFocus={false}
                disabled={!item.active}
                onChange={(value: string) => handleChangeProduct(sourceIndex, "startTime", value)}
                value={startTime}
              />
            </div>
          ),
          endedAt: (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: 5,
              }}
            >
              <DateTextBox
                id={`end-date-${sourceIndex}`}
                name="endDate"
                useAdDate
                inputStyle={{ width: "135px" }}
                disabled={!item.active}
                placeholder={"mm/dd/yyyy"}
                onChange={(value) => handleChangeProduct(sourceIndex, "endDate", value)}
                value={endDate}
              />
              <TimeFreeTextBox24
                id={`end-time-${sourceIndex}`}
                className="affiliate-time-picker"
                name="endTime"
                autoFocus={false}
                disabled={!item.active}
                onChange={(value: string) => handleChangeProduct(sourceIndex, "endTime", value)}
                value={endTime}
              />
            </div>
          ),
          activeProduct: (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <Checkbox
                toggle
                checked={!!item.active}
                name="active"
                onChange={(_event, data) =>
                  handleChangeProduct(sourceIndex, "active", data.checked)
                }
              />
            </div>
          ),
        };
      });
  }, [
    props.AffiliateSettingsSequence?.affiliateProduct,
    props.AffiliateSettingsSequence?.filterAffiliate?.searchProduct,
  ]);

  const handleSelectedItem = useCallback(
    (value: any) => {
      const affiliate =
        props.searchedItemListWithKey?.AffiliateList_Affiliate_Group?.find(
          (item: any) => item.id === value,
        ) || null;

      props.runSequence({
        sequence: "AffiliateSettings",
        action: "SEARCH_AFFILIATE_LIST",
        data: affiliate,
      });
    },
    [props.searchedItemListWithKey],
  );

  const handleGetTdPropAaffiliateProductData = () => {
    return {
      style: {
        cursor: "pointer",
        overflow: "visible",
      },
    };
  };

  const handleOpenModalDate = () => {
    props.runSequence({
      sequence: "AffiliateSettings",
      action: "RESET_SETTING_DATE_PRODUCT",
      onSuccess: () => {
        setOpenModalDate(true);
      },
    });
  };

  const handleChangeFilterData = (_event: any, data: any) => {
    const { name, value, checked } = data;

    props.setProp(
      `AffiliateSettingsSequence.filterAffiliate.${name}`,
      checked !== undefined ? checked : value,
    );
  };

  const handleChangeSettingDateProductValue = (
    name: "startDate" | "startTime" | "endDate" | "endTime",
    value: string,
  ) => {
    props.setProp(`AffiliateSettingsSequence.settingDateProduct.${name}`, value);
  };

  const handleUpdateDateAllProduct = () => {
    props.runSequence({
      sequence: "AffiliateSettings",
      action: "UPDATE_DATE_ALL_PRODUCT",
      onSuccess: () => {
        setOpenModalDate(false);
      },
    });
  };

  const handleSaveAffiliateProduct = () => {
    props.runSequence({
      sequence: "AffiliateSettings",
      action: "SAVE_AFFILIATE_PRODUCT",
    });
  };

  return (
    <div className="CardAffiliateSettings">
      <CardAffiliateSettingsUX
        filterAffiliate={props.AffiliateSettingsSequence?.filterAffiliate}
        affiliateGroupListViewOption={affiliateGroupListViewOption}
        affiliateProductData={affiliateProductData}
        getTdPropAaffiliateProductData={handleGetTdPropAaffiliateProductData}
        affiliateProductLength={(props.AffiliateSettingsSequence?.affiliateProduct || []).length}
        onChangeData={handleChangeFilterData}
        buttonOpenModalDate={handleOpenModalDate}
        searchAffiliateGroup={
          <SearchBoxDropdown
            onEvent={props.onEvent}
            id="Affiliate_Group"
            icon="search"
            limit={20}
            selectedItem={selectedAffiliate?.id || null}
            style={{ width: "100%" }}
            type="AffiliateList"
            fluid
            inline
            useWithKey
            clearable
            searchedItemListWithKey={props.searchedItemListWithKey}
            setSelectedItem={handleSelectedItem}
            onAdditionalUrlParams={() => ({
              active: true,
            })}
            onClear={() => {
              props.setProp("AffiliateSettingsSequence", {
                ...props.AffiliateSettingsSequence,
                filterAffiliate: {
                  searchAffiliate: {},
                  tierAffiliate: null,
                  searchProduct: "",
                },
                affiliateProduct: [],
              });
            }}
            mapOptions={mapOptions}
            placeholder="เลือก Affiliate"
          />
        }
        buttonSave={
          <ButtonLoadCheck
            // function
            setProp={props.setProp}
            onClick={handleSaveAffiliateProduct}
            // data
            paramKey={BTN_ACTS.SAVE_AFFILIATE_PRODUCT}
            buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.SAVE_AFFILIATE_PRODUCT]}
            // config
            style={{ background: "var(--primary-theme-color)", color: "#fff" }}
            size="small"
            title={
              <div style={{ display: "flex", alignItems: "center" }}>
                <Icon name="save"></Icon>
                <label>บันทึกข้อมูล</label>
              </div>
            }
          />
        }
      />

      <Modal
        className="CardAffiliateSettingsModal"
        size="tiny"
        open={openModalDate}
        onClose={() => setOpenModalDate(false)}
      >
        <div style={{ padding: "20px" }}>
          <label style={{ fontWeight: "bold" }}>ตั้งวันที่และเวลาสำหรับสินค้าที่เปิดขาย</label>
          <hr style={{ margin: "15px 0px", color: "#bdbdbd" }} />
          <div
            style={{
              backgroundColor: "#FAE9ED",
              color: "#D16184",
              padding: "10px 15px",
              borderRadius: "10px",
            }}
          >
            มีผลต่อเฉพาะกับสินค้าที่เปิดขายอยู่และกำลังแสดงผลในตารางปัจจุบันเท่านั้น
          </div>

          <div style={{ margin: "15px 0px 10px 0px" }}>
            <label style={{ fontWeight: "bold" }}>วันที่และเวลาเริ่ม</label>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            <DateTextBox
              name="startDate"
              autoAdBe
              useAdDate
              onChange={(value: string) => handleChangeSettingDateProductValue("startDate", value)}
              value={props.AffiliateSettingsSequence?.settingDateProduct?.startDate}
            />
            <TimeFreeTextBox24
              className="affiliate-time-picker"
              name="startTime"
              autoFocus={false}
              onChange={(value: string) => handleChangeSettingDateProductValue("startTime", value)}
              value={props.AffiliateSettingsSequence?.settingDateProduct?.startTime || ""}
            />
          </div>
          <div style={{ margin: "15px 0px 10px 0px" }}>
            <label style={{ fontWeight: "bold" }}>วันที่และเวลาสิ้นสุด</label>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 5,
            }}
          >
            <DateTextBox
              name="endDate"
              autoAdBe
              useAdDate
              onChange={(value: string) => handleChangeSettingDateProductValue("endDate", value)}
              value={props.AffiliateSettingsSequence?.settingDateProduct?.endDate}
            />
            <TimeFreeTextBox24
              className="affiliate-time-picker"
              name="endTime"
              autoFocus={false}
              onChange={(value: string) => handleChangeSettingDateProductValue("endTime", value)}
              value={props.AffiliateSettingsSequence?.settingDateProduct?.endTime || ""}
            />
          </div>
          <hr style={{ margin: "15px 0px", color: "#bdbdbd" }} />
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              onClick={() => setOpenModalDate(false)}
              style={{
                background: "#fff",
                border: "1px solid #bdbdbd",
              }}
            >
              ยกเลิก
            </Button>
            <ButtonLoadCheck
              // function
              setProp={props.setProp}
              onClick={handleUpdateDateAllProduct}
              // data
              paramKey={BTN_ACTS.UPDATE_DATE_ALL_PRODUCT}
              buttonLoadCheck={props.buttonLoadCheck?.[BTN_ACTS.UPDATE_DATE_ALL_PRODUCT]}
              // config
              style={{ background: "var(--primary-theme-color)", color: "#fff" }}
              size="small"
              title={"นำไปใช้ต่อ"}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default memo(CardAffiliateSettings);
